import React, { useEffect, useMemo, useState } from 'react';
import { useBreadcrumbs, useUser } from '../hooks';
import { Link, useParams, useLocation } from 'react-router-dom';
import { Header, Groupper, Modal, Toast, Field, Message, Toolbar, Button, Label, Table, Icon, Checkbox, Input } from 'react-frontier';
import { bindClick, formatAddress, relativeTime, addCommas, statusColor } from '../SiteUtil';
import { EventDate, UserClass, SetLoading, EventStatusType, Location, PromoterContact, EventPayout, EventWorkerRequest, EventDateList, Request as InventoryRequest, Issue } from '../SiteClasses';
import { Dropdown } from 'semantic-ui-react';
import { EventForm, SearchUserModal, SearchPromoterContactModal, PromoterContactModal, Autocomplete, BudgetTable, PayoutsTable, ExpensesTable, EvidenceTable, EventComments, SearchEventModal } from '../components';

import UserAccess from '../UserAccess';
import API from '../API';
import moment from 'moment';
import WorkersTable from '../components/WorkersTable';
import Validator from '../Validator';

enum Modals {
	STATUS = 1,
	CLOSE = 2,
	ASSIGN_COORDINATOR = 4,
	ASSIGN_PROMOTER_CONTACT = 5,
	LOCATION = 6,
	VIEW_PROMOTER_CONTACT = 7,
	ASSIGN_EXECUTIVE = 8,
	ASSIGN_PARENT_EVENT = 9,
	UNASSIGN_EVENT = 10,
	ADD_WORKER_REQUEST = 11,
	DELETE_WORKER_REQUEST = 12,
	VIEW_WORKER_REQUEST = 13,
	VIEW_INVENTORY_REQUEST = 14,
	DATE = 15,
}

enum RightControl {
	WORKERS = 1,
	EXPENSES = 2,
	COMMENTS = 3,
	ASSETS = 4,
	FORM = 5,
}

const SidebarControl = {
	'details': RightControl.COMMENTS,
	'workers': RightControl.WORKERS,
	'expenses': RightControl.EXPENSES,
	'comments': RightControl.COMMENTS,
	'assets': RightControl.ASSETS,
	'form': RightControl.FORM,
}

var EventView = () => {
	var params = useParams<{ id: string }>();
	var location = useLocation();
	var { setTitle } = useBreadcrumbs();
	var { hasAccess, user } = useUser();
	var [loadError, setLoadError] = useState<string>(null);
	var [eventData, setEventData] = useState<EventDate>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [shownModal, setShownModal] = useState<number>(null);
	var [selectedStatus, setSelectedStatus] = useState<EventStatusType>(null);
	var [newLocation, setNewLocation] = useState<Location>(null);
	var [rightControl, setRightControl] = useState<RightControl>(RightControl.COMMENTS);
	var [selectedChild, setSelectedChild] = useState<{date_id: number, event_name: string, date: number}>(null);
	var [closePayouts, setClosePayouts] = useState<EventPayout[]>(null);
	var [closeConfirmed, setCloseConfirmed] = useState<boolean>(false);
	var [closeLoading, setCloseLoading] = useState<boolean>(false);
	var [workerRequests, setWorkerRequests] = useState<EventWorkerRequest[]>(null);
	var [issues, setIssues] = useState<Issue[]>(null);
	var [selectedRequest, setSelectedRequest] = useState<EventWorkerRequest>(null);
	var [selectedInventoryRequest, setSelectedInventoryRequest] = useState<InventoryRequest>(null);
	var [newRequestTitleId, setNewRequestTitleId] = useState<number>(null);
	var [newRequestQuantity, setNewRequestQuantity] = useState<string>('');
	var [workerTitles, setWorkerTitles] = useState<{ key: number, text: string, value: number }[]>(null);
	var [newDate, setNewDate] = useState<number>(null);

	useEffect(() => {
		if (!eventData || eventData?.date_id !== parseInt(params.id)) {
			setEventData(null);
			setWorkerRequests(null);
			API.getEvent(parseInt(params.id), true).then(res=>{
				if(res.error) setLoadError(res.message);
				setTitle(res.data.event_name);
				setEventData(res.data);
				setWorkerRequests(res.data.requested_workers || []);
				setIssues(res.data.issues || []);
			}).catch(()=>{
				setLoadError('Hubo un error cargando el evento (LCL-1)');
			})
		}
	}, [params.id]);

	useEffect(()=>{
		var section = location.pathname.split('/').pop();
		setRightControl(SidebarControl[section as keyof typeof SidebarControl] || RightControl.COMMENTS);
	}, [location.pathname]);

	var showStatusModal = () => {
		setSelectedStatus(eventData.status_id);
		setShownModal(Modals.STATUS);
	}

	var showCloseModal = () => {
		setModalPrompts(null);
		setCloseConfirmed(false);
		setClosePayouts(null);
		setCloseLoading(true);
		setShownModal(Modals.CLOSE);
		API.getEventPayouts(eventData.date_id, 100, 0).then(res=>{
			if(res.error) return;
			setClosePayouts(res.data);
		}).catch(()=>{}).finally(()=>{
			setCloseLoading(false);
		});
	}

	var updateStatus = (setLoading: SetLoading) => {
		if (!selectedStatus) return setModalPrompts(['Selecciona un estatus'])

		setLoading(true);
		API.updateEventStatus(eventData.date_id, selectedStatus).then(res => {
			if (res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha actualizado el estatus del evento');
			var e = { ...eventData };
			e.status_id = selectedStatus;
			setEventData(e);
			setShownModal(null);
		}).catch(err => {
			setModalPrompts(['Hubo un error inesperado actualizando el estatus (LCL-1)']);
		}).finally(() => {
			setLoading(false);
		})
	}

	var showDateModal = () => {
		setNewDate(eventData.date);
		setModalPrompts(null);
		setShownModal(Modals.DATE);
	}

	var updateEventDate = (setLoading: SetLoading) => {
		if (!newDate) return setModalPrompts(['Selecciona una fecha']);

		setLoading(true);
		API.updateEventDate(eventData.date_id, newDate).then(res => {
			if (res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha actualizado la fecha del evento');
			var e = { ...eventData };
			e.date = newDate;
			setEventData(e);
			setShownModal(null);
		}).catch(err => {
			setModalPrompts(['Hubo un error inesperado actualizando la fecha (LCL-1)']);
		}).finally(() => {
			setLoading(false);
		})
	}

	var closeEvent = (setLoading: SetLoading) => {
		setLoading(true);
		API.closeEvent(eventData.date_id).then(res => {
			if (res.data?.errors && res.data.errors.length > 0) {
				return setModalPrompts(res.data.errors);
			}
			if (res.error) {
				return setModalPrompts([res.message]);
			}
			Toast.success('Se ha cerrado el evento');
			var e = { ...eventData };
			e.closed = true;
			setEventData(e);
			setShownModal(null);
		}).catch(err => {
			setModalPrompts(['Hubo un error inesperado cerrando el evento (LCL-1)']);
		}).finally(() => {
			setLoading(false);
		})
	}

	var assignCoordinator = (user: UserClass) => {
		var tx = Toast.show('Asignando coordinador', {
			loading: true,
			dismissable: false,
			infinite: true
		});
		setShownModal(null);
		API.assignEventCoordinator(eventData.date_id, user.user_id).then(res => {
			if (res.error) return Toast.error(res.message);
			Toast.success(`Se ha asignado a ${user.name} como coordinador`);

			var e = { ...eventData };
			e.user_coordinator = user.user_id;
			e.user_coordinator_name = user.name;
			setEventData(e);
		}).catch(err => {
			return Toast.error('Hubo un error asignando al coordinador (LCL-1)');
		}).finally(() => {
			Toast.dismiss(tx);
		})
	}

	var assignExecutive = (user: UserClass) => {
		var tx = Toast.show('Asignando ejecutivo comercial', {
			loading: true,
			dismissable: false,
			infinite: true
		});
		setShownModal(null);
		API.assignEventExecutive(eventData.date_id, user.user_id).then(res => {
			if (res.error) return Toast.error(res.message);
			Toast.success(`Se ha asignado a ${user.name} como ejecutivo comercial`);

			var e = { ...eventData };
			e.user_executive = user.user_id;
			e.user_executive_name = user.name;
			setEventData(e);
		}).catch(err => {
			return Toast.error('Hubo un error asignando al ejecutivo (LCL-1)');
		}).finally(() => {
			Toast.dismiss(tx);
		})
	}

	var assignPromoterContact = (contact: PromoterContact) => {
		var tx = Toast.show('Asignando contacto de promotor', {
			loading: true,
			dismissable: false,
			infinite: true
		});
		setShownModal(null);
		API.assignEventPromoterContact(eventData.date_id, contact.contact_id).then(res => {
			if (res.error) return Toast.error(res.message);
			Toast.success(`Se ha asignado a ${contact.contact_name} como contacto de promotor`);

			var e = { ...eventData };
			e.promoter_contact = contact.contact_id;
			e.promoter_contact_name = contact.contact_name;
			setEventData(e);
		}).catch(err => {
			return Toast.error('Hubo un error asignando al contacto de promotor (LCL-1)');
		}).finally(() => {
			Toast.dismiss(tx);
		})
	}

	var assignParentEvent = (parent: EventDateList)=>{
		setShownModal(null);
		API.assignParentEvent(eventData.date_id, parent.date_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success(`Se ha asignado el evento "${parent.event_name}" como padre`);

			var e = {...eventData};
			e.parent_date_id = parent.date_id;
			e.parent_event_name = parent.event_name;
			setEventData(e);
		}).catch(err=>{
			return Toast.error('Hubo un error asignando el evento padre (LCL-1)');
		})
	}

	var addLocation = (setLoading: SetLoading)=>{
		if(!newLocation) return setModalPrompts(['Selecciona una ubicación']);
		setLoading(true);
		API.addEventLocation(eventData.date_id, newLocation).then(res => {
			if (res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha actualizado el evento');

			var e = { ...eventData };
			e.location = newLocation;
			e.location_id = newLocation.location_id;
			setEventData(e);
			setShownModal(null);
		}).catch(err => {
			return setModalPrompts(['Hubo un error inesperado agregando la ubicación (LCL-1)']);
		}).finally(() => {
			setLoading(false);
		})
	}

	var unassignEvent = (setLoading: SetLoading)=>{
		setLoading(true);
		API.unassignEvent(selectedChild.date_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha desvinculado el evento');

			var e = {...eventData};
			e.related_events = e.related_events.filter(ev=>ev.date_id!==selectedChild.date_id);
			setEventData(e);
			setShownModal(null);
		}).catch(err=>{
			return setModalPrompts(['Hubo un error desvinculando el evento (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var showAddRequestModal = ()=>{
		setNewRequestTitleId(null);
		setNewRequestQuantity('');
		setModalPrompts(null);
		setShownModal(Modals.ADD_WORKER_REQUEST);
		if(!workerTitles){
			API.getTitles(100, 0).then(res=>{
				if(res.error) return;
				setWorkerTitles(res.data.map(t => ({ key: t.title_id, text: t.title_name, value: t.title_id })));
			}).catch(()=>{});
		}
	}

	var addWorkerRequest = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator({ title_id: newRequestTitleId, quantity: newRequestQuantity }, {
			title_id: [{ rule: 'number', label: 'Posición' }],
			quantity: [{ rule: 'min', params: [1], label: 'Cantidad' }],
		});
		setModalPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.addEventWorkerRequest(eventData.date_id, newRequestTitleId, parseInt(newRequestQuantity)).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Solicitud agregada');

			var selectedTitle = workerTitles?.find(t => t.value === newRequestTitleId);
			var newRequest: EventWorkerRequest = {
				request_id: res.data.request_id,
				date_id: eventData.date_id,
				title_id: newRequestTitleId,
				title_name: selectedTitle?.text || '',
				quantity: parseInt(newRequestQuantity),
				deleted: false,
				user_creator: user.user_id,
				user_creator_name: `${user.first_name} ${user.last_name}`,
				date_created: Math.floor(Date.now() / 1000),
			};
			setWorkerRequests(prev => [...(prev || []), newRequest]);
			setShownModal(null);
		}).catch(()=>{
			setModalPrompts(['Hubo un error agregando la solicitud (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var deleteWorkerRequest = (setLoading: SetLoading)=>{
		setLoading(true);
		API.deleteEventWorkerRequest(selectedRequest.request_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setWorkerRequests(prev => prev.filter(r => r.request_id !== selectedRequest.request_id));
			setShownModal(null);
			Toast.success('Solicitud eliminada');
		}).catch(()=>{
			setModalPrompts(['Hubo un error eliminando la solicitud (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var groupedRequests = useMemo(()=>{
		if(!workerRequests) return [];
		return workerRequests.reduce((acc, req)=>{
			var group = acc.find(g => g.title_id === req.title_id);
			if(!group){
				acc.push({ title_id: req.title_id, title_name: req.title_name, total: req.quantity, requests: [req] });
			} else {
				group.total += req.quantity;
				group.requests.push(req);
			}
			return acc;
		}, [] as { title_id: number, title_name: string, total: number, requests: EventWorkerRequest[] }[]);
	}, [workerRequests]);
	var inventoryRelations = eventData?.inventory_relations || [];

	var openInventoryRequestInfo = (request_id: number)=>{
		API.getRequest(request_id).then(res=>{
			if(res.error || !res.data) return Toast.error(res.message || 'No se pudo cargar la solicitud.');
			setSelectedInventoryRequest(res.data as InventoryRequest);
			setShownModal(Modals.VIEW_INVENTORY_REQUEST);
		}).catch(()=>{
			Toast.error('Hubo un error cargando la solicitud.');
		});
	};

	var control_header = useMemo(()=>{
		switch(rightControl){
			case RightControl.ASSETS: return 'Evidencias';
			case RightControl.COMMENTS: return 'Comentarios';
			case RightControl.EXPENSES: return 'Gastos';
			case RightControl.FORM: return 'Formulario';
			case RightControl.WORKERS: return 'Trabajadores';
		}
	}, [rightControl]);

	if (loadError) return <Header text='Error' subtext={loadError} />
	if (!eventData) return <Header text='Cargando datos' loading />

	if(eventData.parent_date_id) return <Header style={{ maxWidth: 600, margin: 'auto',  marginTop: 60 }} size='large' text={'Evento vinculado a un evento padre'} subheaderStyle={{marginTop: 10 }} subtext={'Este evento se encuentra asociado a un evento padre. Para consultar información detallada, registros y gestionar operaciones relacionadas con este evento, por favor acceda al evento padre correspondiente.'} actions={
		<Button style={{ marginTop: 20 }} iconName='external-link' text='Ver evento padre' as={Link} to={`/events/${eventData.parent_date_id}`} color='black'/>
	}/>

	return <div className='ar event-view-screen'>
		<div className="left">
			<Groupper width={600} fitted title={'Datos de evento'} style={{ marginTop: 0 }}>
				<Toolbar stretch style={{ height: 40 }}>
					<Toolbar.Dropdown
						text='Editar'
						iconName='pen'
						items={[
							...(hasAccess(UserAccess.EVENTS.CHANGE_EVENT_STATUS) ? [{
								text: 'Estatus',
								onClick: showStatusModal
							}] : []),
							...(hasAccess(UserAccess.EVENTS.EDIT_EVENTS) && (eventData.status_id === EventStatusType.OPERATING || eventData.status_id === EventStatusType.PROMOTER_OPERATED) ? [{
								text: 'Ubicación',
								onClick: bindClick(setShownModal, Modals.LOCATION)
							}] : []),
							...(hasAccess(UserAccess.EVENTS.EDIT_EVENTS) && (!eventData.related_events || eventData.related_events.length === 0) ? [{
								text: 'Evento padre',
								onClick: bindClick(setShownModal, Modals.ASSIGN_PARENT_EVENT)
							}] : []),
							...(user?.superadmin ? [{
								text: 'Fecha',
								onClick: showDateModal
							}] : [])
						]}
					/>
					<Toolbar.Dropdown
						text='Asignar'
						iconName='user-plus'
						items={[
							...(hasAccess(UserAccess.EVENTS.ASSIGN_EVENT_COORDINATOR) ? [{
								text: 'Coordinador',
								onClick: bindClick(setShownModal, Modals.ASSIGN_COORDINATOR)
							}] : []),
							...(hasAccess(UserAccess.EVENTS.ASSIGN_EVENT_COORDINATOR) ? [{
								text: 'Ejecutivo comercial',
								onClick: bindClick(setShownModal, Modals.ASSIGN_EXECUTIVE)
							}] : []),
							...(hasAccess(UserAccess.EVENTS.ASSIGN_EVENT_COORDINATOR) && eventData.promoter_id ? [{
								text: 'Contacto Promotor',
								onClick: bindClick(setShownModal, Modals.ASSIGN_PROMOTER_CONTACT)
							}] : [])
						]}
					/>
					{hasAccess(UserAccess.EVENTS.CLOSE_EVENTS) && !eventData.closed && (
						<Toolbar.Item text='Cerrar' iconName='scissors' onClick={showCloseModal}/>
					)}
				</Toolbar>
			<Table fitted striped details data={[
				['Calendario', <>
					{eventData.date_id}
					<div className="meta">
						<Button
							size='tiny'
							text='Ver'
							iconName='external-link'
							as={Link}
							to={`https://checkout.arema.mx/${eventData.date_id}`}
							target="_blank"
							style={{ marginRight: '10px !important' }}
						/>
					</div>
				</>],
						['Nombre', eventData.event_name],
						['Promotor', eventData.promoter],
						['Boletos', <><Icon name='ticket'/><span style={{ fontWeight: 'bold', fontSize: 15 }}>{eventData.tickets_sold + eventData.related_events?.reduce((acc, e) => acc + e.tickets_sold, 0)}</span></>],
						['Cortesías', <><Icon name='ticket' color='green'/><span style={{ fontWeight: 'bold', fontSize: 15 }}>{eventData.tickets_free + eventData.related_events?.reduce((acc, e) => acc + e.tickets_free, 0)}</span></>],
						['Coordinador', eventData.user_coordinator ? (
						<div className="ar user">
							<div className="image">
								<Icon name='user' />
							</div>
							<div className="data">
								<div className="name">{eventData.user_coordinator_name}</div>
								<div className="job">Coordinador</div>
							</div>
						</div>
					) : (
						<Button text='Asignar coordinador' color='red' onClick={bindClick(setShownModal, Modals.ASSIGN_COORDINATOR)} />
					)],
					['Ejecutivo', eventData.user_executive ? (
						<div className="ar user">
							<div className="image">
								<Icon name='user' />
							</div>
							<div className="data">
								<div className="name">{eventData.user_executive_name}</div>
								<div className="job">Ejecutivo comercial</div>
							</div>
						</div>
					) : (
						<Button text='Asignar ejecutivo' color='green' onClick={bindClick(setShownModal, Modals.ASSIGN_EXECUTIVE)} />
					)],
					...(eventData.promoter_id ? [
						['Contacto Promotor', eventData.promoter_contact ? (
							<div className="ar user" onClick={bindClick(setShownModal, Modals.VIEW_PROMOTER_CONTACT)}>
								<div className="image">
									<Icon name='address-book' />
								</div>
								<div className="data">
									<div className="name">{eventData.promoter_contact_name}</div>
									<div className="job">Contacto de Promotor</div>
								</div>
							</div>
						) : eventData.promoter_id ? (
							<Button text='Asignar contacto' color='blue' onClick={bindClick(setShownModal, Modals.ASSIGN_PROMOTER_CONTACT)} />
						) : (
							'Sin promotor asignado'
						)],
					] : []),
					...eventData.promoter_contact_phone ? [
						['Teléfono contacto', eventData.promoter_contact_phone],
					] : [],
					[],
				['Fecha', <>
					{moment.unix(eventData.date).format('DD/MMM/YY HH:mm')}
					<div className="meta">
						{relativeTime(eventData.date)}
					</div>
				</>],
				['Estatus', <>
					<Label color={
						eventData.closed ? 'red' :
						eventData.status_id === EventStatusType.OPERATING ? 'yellow' :
						eventData.status_id === EventStatusType.PROMOTER_OPERATED ? 'blue' :
						eventData.status_id === EventStatusType.POSTPONED ? 'orange' :
						eventData.status_id === EventStatusType.CANCELLED ? 'red' :
						eventData.status_id === EventStatusType.NOT_OPERATED ?  'black' : null
					}>
					{eventData.closed ? 'Cerrado' :
						eventData.status_id === EventStatusType.OPERATING ? 'Operado por AREMA' :
						eventData.status_id === EventStatusType.PROMOTER_OPERATED ? 'Operado por Promotor' :
						eventData.status_id === EventStatusType.POSTPONED ? 'Pospuesto' :
						eventData.status_id === EventStatusType.CANCELLED ? 'Cancelado' :
						eventData.status_id === EventStatusType.NOT_OPERATED ? 'No Operado' : 'Sin estatus'}
				</Label>
				</>],
				['Dirección', eventData.location ? formatAddress(eventData.location) : 'Sin dirección'],
				]} />
				{eventData.related_events && eventData.related_events.length > 0 && (
					<Table
						title='Eventos relacionados'
						fitted
						striped
						headers={['ID', 'Evento', 'Boletos', 'Cortesias', 'Hora', <Icon name='wrench' />]}
						collapsingIndexes={[0, 2]}
					>
						<Table.Row
							centeredIndexes={[2, 3]}
							data={[
								eventData.date_id,
								<><Icon name='crown'/>{eventData.event_name}</>,
								eventData.tickets_sold,
								eventData.tickets_free,
								moment.unix(eventData.date).format('HH:mm'),
							]}
						/>
						{eventData.related_events.map(event => (
							<Table.Row
								key={event.date_id}
								centeredIndexes={[2, 3]}
								data={[
									event.date_id,
									event.event_name,
									event.tickets_sold,
									event.tickets_free,
									moment.unix(event.date).format('HH:mm'),
									<Button iconName='close' icon size='tiny' basic onClick={() => {setSelectedChild(event); setShownModal(Modals.UNASSIGN_EVENT)} }/>
								]}
							/>
						))}
					</Table>
				)}
				{hasAccess(UserAccess.EVENTS.VIEW_EVENTS) && (
					<Table
						title='Solicitud de personal'
						fitted
						striped
						headers={['Posición', 'Cantidad', <Icon name='wrench' />]}
						collapsingIndexes={[1, 2]}
						centeredIndexes={[2]}
						button={hasAccess(UserAccess.EVENTS.CREATE_WORKER_REQUEST) ? (
							<Button size='tiny' color='black' iconName='circle-plus' text='Agregar' onClick={showAddRequestModal} />
						) : undefined}
					>
						{(!workerRequests || workerRequests.length === 0) ? (
							<Table.Cell row className='empty' colSpan={3}>
								No hay solicitudes registradas
							</Table.Cell>
						) : groupedRequests.map(group => (
							<Table.Row
								key={group.title_id}
								centeredIndexes={[1]}
								data={[
									group.title_name,
									group.total,
									<Dropdown direction='left' icon={(
										<div className="fr label" style={{ padding: '3px 10px' }}>
											<Icon name='ellipsis' style={{ margin: 0 }} />
										</div>
									)} className='icon'>
										<Dropdown.Menu>
											<Dropdown.Item onClick={() => { setSelectedRequest(group.requests[0]); setModalPrompts(null); setShownModal(Modals.VIEW_WORKER_REQUEST); }}>
												<Icon name='info' style={{ marginRight: 3 }} /> Info
											</Dropdown.Item>
										</Dropdown.Menu>
									</Dropdown>
								]}
							/>
						))}
					</Table>
				)}
				{hasAccess(UserAccess.EVENTS.VIEW_EVENTS) && (
					<Table
						title='Solicitudes de equipo y envíos'
						fitted
						striped
						headers={['ID', 'Tipo', 'Estatus', <Icon name='wrench' />]}
						collapsingIndexes={[0, 2, 3]}
						centeredIndexes={[0, 2, 3]}
					>
						{inventoryRelations.length === 0 ? (
							<Table.Cell row className='empty' colSpan={4}>
								No hay solicitudes ni envíos registrados
							</Table.Cell>
						) : inventoryRelations.map(relation => {
							var id = relation.request ? relation.request_id : relation.shipment_id;
							var type = relation.request ? 'Solicitud' : 'Envío';
							var status = relation.request
								? relation.sent
									? 'Enviada'
									: relation.rejected
									? 'Rechazado'
									: relation.approved
									? 'Aprobado'
									: 'Pendiente'
								: 'Registrado';
							return (
								<Table.Row
									key={`${type}-${id}`}
									centeredIndexes={[0, 2, 3]}
									data={[
										id,
										type,
										<Label value={status} style={{ width: 100 }} color={
											relation.request
												? relation.sent
													? 'green'
													: relation.rejected
													? 'red'
													: relation.approved
													? 'blue'
													: 'yellow'
												: 'blue'
										} />,
										(
											relation.request
												? hasAccess(UserAccess.INVENTORY.VIEW_INVENTORY_REQUESTS)
												: hasAccess(UserAccess.INVENTORY.VIEW_INVENTORY_SHIPMENTS)
										) ? (
											<Dropdown direction='left' icon={(
												<div className="fr label" style={{ padding: '3px 10px' }}>
													<Icon name='ellipsis' style={{ margin: 0 }} />
												</div>
											)} className='icon'>
												<Dropdown.Menu>
													<Dropdown.Item
														as={Link}
														to={relation.request ? `/requests/${relation.request_id}` : `/shipments/${relation.shipment_id}`}
													>
														<Icon name='eye' style={{ marginRight: 3 }} /> Ver
													</Dropdown.Item>
													{relation.request ? (
														<Dropdown.Item onClick={() => openInventoryRequestInfo(relation.request_id!)}>
															<Icon name='info-circle' style={{ marginRight: 3 }} /> Info
														</Dropdown.Item>
													) : null}
												</Dropdown.Menu>
											</Dropdown>
										) : null
									]}
								/>
							);
						})}
					</Table>
				)}
				{hasAccess(UserAccess.ISSUES.VIEW_ISSUES)&&(
					<Table
						striped
						centeredIndexes={[0, 2, 3]}
						collapsingIndexes={[0, 3]}
						title='Tareas relacionadas con el evento'
						headers={['ID', 'Título', 'Estatus', <Icon name='wrench' />]}
					>
						{issues.length===0?(
							<Table.Cell row className='empty' colSpan={4}>
								No hay tareas relacionadas con el evento
							</Table.Cell>
						):issues.map(issue=>(
							<Table.Row  
								key={'issue-'+issue.issue_id}
								centeredIndexes={[0, 2, 3]}
								data={[
									issue.issue_id,
									issue.title.slice(0, 50) + (issue.title.length>49 ? '...' : ''),
									<Label value={issue.status.status_name} color={statusColor(issue.status.status_id)} />,
									<Dropdown direction='left' icon={(
										<div className="fr label" style={{ padding: '3px 10px' }}>
											<Icon name='ellipsis' style={{ margin: 0 }} />
										</div>
									)} className='icon'>
										<Dropdown.Menu>
											<Dropdown.Item
												as={Link}
												to={`/issues/${issue.issue_id}`}
											>
												<Icon name='eye' style={{ marginRight: 3 }} /> Ver
											</Dropdown.Item>
										</Dropdown.Menu>
									</Dropdown>
								]}
							/>
						))}
					</Table>
				)}
			</Groupper>
		</div>
		<div className="right">
			<div className="header">
				{control_header}
			</div>
			<div className="content">
				{rightControl === RightControl.WORKERS ? <>
					{hasAccess(UserAccess.EVENTS.VIEW_EVENT_WORKERS) && (
						<WorkersTable dateId={eventData.date_id} />
					)}
					<PayoutsTable dateId={eventData.date_id} user_coordinator={eventData.user_coordinator} />
				</> : rightControl === RightControl.EXPENSES ? <>
					{hasAccess(UserAccess.EVENTS.VIEW_BUDGET) && (
						<BudgetTable dateId={eventData.date_id} eventName={eventData.event_name} />
					)}
					{hasAccess(UserAccess.EVENTS.VIEW_EXPENSE) && (
						<ExpensesTable dateId={eventData.date_id} eventName={eventData.event_name} />
					)}
				</> : rightControl === RightControl.COMMENTS ? (
					hasAccess(UserAccess.EVENTS.VIEW_COMMENTS) && (
						<EventComments dateId={eventData.date_id} />
					)
				) : rightControl === RightControl.FORM ? (
					<EventForm date_id={eventData.date_id} />
				) : rightControl === RightControl.ASSETS ? (
					<EvidenceTable dateId={eventData.date_id} />
				) : null}
			</div>
		</div>
		<Modal open={shownModal == Modals.STATUS} size='tiny' onClose={bindClick(setShownModal)}>
			<Modal.Header>Actualizar estatus</Modal.Header>
			<Modal.Content>
				<Field label='Estatus'>
					<Dropdown
						selection
						value={selectedStatus}
						onChange={(e, data) => setSelectedStatus(Number(data.value))}
						placeholder='Estatus de evento'
						options={[
							{ text: 'Operado por AREMA', value: EventStatusType.OPERATING, key: EventStatusType.OPERATING },
							{ text: 'Operado por Promotor', value: EventStatusType.PROMOTER_OPERATED, key: EventStatusType.PROMOTER_OPERATED },
							{ text: 'Pospuesto', value: EventStatusType.POSTPONED, key: EventStatusType.POSTPONED },
							{ text: 'Cancelado', value: EventStatusType.CANCELLED, key: EventStatusType.CANCELLED },
							{ text: 'No operado', value: EventStatusType.NOT_OPERATED, key: EventStatusType.NOT_OPERATED },
						]}
					/>
				</Field>
				<Message style={{ marginTop: 15 }} type='error' list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={bindClick(setShownModal, null)} />
				<Button text='Guardar' color='black' onClick={updateStatus} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.CLOSE} onClose={bindClick(setShownModal)} size='small'>
			<Modal.Header>Cerrar evento</Modal.Header>
			<Modal.Content>
				<Header
					text='¿Marcar evento como cerrado?'
					subtext='Solo usar cuando la operación del evento haya terminado completamente'
				/>
				{closeLoading ? (
					<Header loading text='Cargando nomina...' />
				) : closePayouts && closePayouts.length > 0 ? (
					<Table
						title='Nomina'
						striped
						headers={['ID', 'Trabajadores', 'Pagado', 'Pendiente', 'Total']}
						collapsingIndexes={[0, 1, 2, 3, 4]}
						centeredIndexes={[1]}
						style={{ marginTop: 15 }}
					>
						{closePayouts.map(p => (
							<Table.Row
								key={p.payout_id}
								centeredIndexes={[1]}
								data={[
									p.payout_id,
									p.worker_count,
									'$' + addCommas(p.paid_amount),
									'$' + addCommas(p.pending_amount),
									'$' + addCommas(p.total_amount),
								]}
							/>
						))}
						<Table.Row centeredIndexes={[1]} data={[
							<b>Total</b>,
							closePayouts.reduce((sum, p) => sum + parseInt(p.worker_count as any), 0),
							<b>{'$' + addCommas(closePayouts.reduce((sum, p) => sum + p.paid_amount, 0))}</b>,
							<b>{'$' + addCommas(closePayouts.reduce((sum, p) => sum + p.pending_amount, 0))}</b>,
							<b>{'$' + addCommas(closePayouts.reduce((sum, p) => sum + p.total_amount, 0))}</b>,
						]}/>
					</Table>
				) : !closeLoading ? (
					<Message type='warning' text='Este evento no tiene nomina registrada.' style={{ marginTop: 15 }} />
				) : null}
				<Checkbox
					label='Esta nomina es correcta y deseo cerrar el evento'
					checked={closeConfirmed}
					onChange={setCloseConfirmed}
					style={{ marginTop: 15 }}
				/>
				<Message type='error' list={modalPrompts}/>
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setShownModal)} />
				<Button text='Guardar' color='black' onClick={closeEvent} disabled={!closeConfirmed}/>
			</Modal.Actions>
		</Modal>
		<SearchUserModal
			open={shownModal === Modals.ASSIGN_COORDINATOR}
			onClose={bindClick(setShownModal)}
			title='Seleccionar coordinador'
			onSelected={assignCoordinator}
			tagOptions={[
				{ tag: 'operaciones', label: 'Operaciones' },
				{ tag: 'comercial', label: 'Comercial' }
			]}
		/>
		<SearchUserModal
			open={shownModal === Modals.ASSIGN_EXECUTIVE}
			onClose={bindClick(setShownModal)}
			title='Seleccionar ejecutivo comercial'
			onSelected={assignExecutive}
			tagOptions={[
				{ tag: 'operaciones', label: 'Operaciones' },
				{ tag: 'comercial', label: 'Comercial' }
			]}
		/>
		<SearchPromoterContactModal
			open={shownModal === Modals.ASSIGN_PROMOTER_CONTACT}
			onClose={bindClick(setShownModal)}
			promoter_id={eventData?.promoter_id || 0}
			onSelected={assignPromoterContact}
		/>
		<PromoterContactModal
			open={shownModal === Modals.VIEW_PROMOTER_CONTACT}
			onClose={bindClick(setShownModal)}
			contact_id={eventData?.promoter_contact || 0}
		/>
		<SearchEventModal
			open={shownModal === Modals.ASSIGN_PARENT_EVENT}
			onClose={bindClick(setShownModal)}
			title='Seleccionar evento padre'
			onSelected={assignParentEvent}
		/>
		<Modal open={shownModal===Modals.DATE} onClose={bindClick(setShownModal)} size='tiny'>
			<Modal.Header>Editar fecha</Modal.Header>
			<Modal.Content>
				<Field label='Fecha del evento'>
					<Input
						value={newDate}
						onChange={setNewDate}
						calendar={{ mode: 'datetime', locale: 'es' }}
						placeholder='Seleccionar fecha...'
					/>
				</Field>
				<Message
					type='warning'
					text='AREMA es la fuente de la fecha: la sincronización automática puede revertir este cambio.'
					style={{ marginTop: 15 }}
				/>
				<Message style={{ marginTop: 15 }} type='error' list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={bindClick(setShownModal, null)} />
				<Button text='Guardar' color='black' onClick={updateEventDate} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.LOCATION} onClose={bindClick(setShownModal)} size='tiny'>
			<Modal.Header>{!eventData.location_id ? 'Agregar ubicación' : 'Editar ubicación'}</Modal.Header>
			<Modal.Content>
				<Field label='Ubicación' comment='Ubicación del recinto'>
					<Autocomplete
						onChange={setNewLocation}
						placeholder='Buscar ubicación...'
						onPlaceSelected={setNewLocation}
						types={['establishment']}
					/>
				</Field>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShownModal, null)} />
				<Button text='Agregar' color='black' onClick={addLocation} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.UNASSIGN_EVENT} onClose={bindClick(setShownModal)} size='tiny'>
			<Modal.Header>Desvincular evento</Modal.Header>
			{selectedChild && <Modal.Content>
				<Header
					text='¿Desvincular evento?'
					subtext='Esta acción eliminará la relación con el evento padre actual'
				/>
				<Table
					details
					striped
					style={{ marginTop: 20 }}
					data={[
						['Calendario', selectedChild?.date_id],
						['Nombre', selectedChild?.event_name],
						['Horario', moment.unix(selectedChild?.date).format('DD/MMM/YY HH:mm')]
					]}
				/>
				<Message type='error' list={modalPrompts}/>
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setShownModal)} />
				<Button text='Guardar' color='black' onClick={unassignEvent}/>
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.ADD_WORKER_REQUEST} onClose={bindClick(setShownModal)} size='tiny'>
			<Modal.Header>Solicitar personal</Modal.Header>
			<Modal.Content>
				<Field label='Posición'>
					<Dropdown
						selection
						search
						value={newRequestTitleId}
						onChange={(e, data) => setNewRequestTitleId(Number(data.value))}
						placeholder='Seleccionar posición...'
						options={workerTitles || []}
					/>
				</Field>
				<Input
					label='Cantidad'
					value={newRequestQuantity}
					onChange={setNewRequestQuantity}
					inputType='number'
					style={{ marginTop: 10 }}
				/>
				<Message type='error' list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setShownModal)} />
				<Button text='Guardar' color='black' onClick={addWorkerRequest} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.DELETE_WORKER_REQUEST} onClose={bindClick(setShownModal)} size='tiny'>
			<Modal.Header>Eliminar solicitud</Modal.Header>
			{selectedRequest && <Modal.Content>
				<Header
					text='¿Eliminar solicitud?'
					subtext='Esta acción no se puede deshacer'
				/>
				<Table
					details
					striped
					style={{ marginTop: 20 }}
					data={[
						['Posición', selectedRequest?.title_name],
						['Cantidad', selectedRequest?.quantity],
					]}
				/>
				<Message type='error' list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setShownModal)} />
				<Button text='Eliminar' color='red' onClick={deleteWorkerRequest} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.VIEW_WORKER_REQUEST} onClose={bindClick(setShownModal)} size='tiny'>
			<Modal.Header>Información de solicitud</Modal.Header>
			{selectedRequest && (() => {
				var group = workerRequests?.filter(r => r.title_id === selectedRequest.title_id) || [];
				var total = group.reduce((sum, r) => sum + r.quantity, 0);
				return <Modal.Content>
					<Table
						details
						striped
						fitted
						data={[
							['Posición', selectedRequest.title_name],
							['Total solicitado', total],
						]}
					/>
					<Table
						fitted
						striped
						title='Solicitudes'
						headers={['Solicitante', 'Cantidad', ...(hasAccess(UserAccess.EVENTS.DELETE_WORKER_REQUEST) ? [<Icon name='wrench' />] : [])]}
						collapsingIndexes={[1, 2, 3]}
						centeredIndexes={[3]}
						style={{ marginTop: 0 }}
					>
						{group.map(req => (
							<Table.Row
								key={req.request_id}
								collapsingIndexes={[1]}
								centeredIndexes={[1]}
								data={[
									req.user_creator_name,
									req.quantity,
									...(hasAccess(UserAccess.EVENTS.DELETE_WORKER_REQUEST) ? [
										<Button iconName='trash' icon  size='tiny' basic onClick={() => { setSelectedRequest(req); setModalPrompts(null); setShownModal(Modals.DELETE_WORKER_REQUEST); }} />
									] : []),
								]}
							/>
						))}
					</Table>
				</Modal.Content>;
			})()}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.VIEW_INVENTORY_REQUEST} onClose={bindClick(setShownModal)} size='tiny'>
			<Modal.Header>Información de solicitud</Modal.Header>
			{selectedInventoryRequest && <Modal.Content style={{ paddingBottom: 0 }}>
				<Table
					details
					striped
					fitted
					data={[
						['ID', selectedInventoryRequest.request_id],
						['Tipo', selectedInventoryRequest.is_express ? 'Solicitud express' : 'Solicitud'],
						['Estatus', selectedInventoryRequest.deleted ? 'Eliminado' : selectedInventoryRequest.rejected ? 'Rechazado' : selectedInventoryRequest.shipment_id ? 'Enviada' : selectedInventoryRequest.approved ? 'Aprobado' : 'Pendiente'],
						['Solicitó', selectedInventoryRequest.user_creator_name || `Usuario #${selectedInventoryRequest.user_creator}`],
						['Fecha solicitud', moment.unix(selectedInventoryRequest.date_created).format('DD/MMM/YY HH:mm')],
						...(selectedInventoryRequest.rejected ? [['Motivo de rechazo', selectedInventoryRequest.rejection_reason || <Icon name='minus' />]] : []),
						...(selectedInventoryRequest.description ? [['Descripción', selectedInventoryRequest.description]] : []),
					]}
				/>
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default EventView;
